import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Container, CssBaseline, InputAdornment, TextField, Typography } from "@material-ui/core";
import { AccountCircle } from "@material-ui/icons";
import Lottie from "react-lottie";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ForgotPasswordLottie from "../../assets/lottie/forgotPassword.json";
import ValidationUtils from "../../utils/classes/ValidationUtils";
import toastError from "../../errors/toastError";

import iconLight from "../../assets/miscPng/icon.png";
import iconDark from "../../assets/miscPng/icon-dark.png";

import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils";
import LottieUtils from "../../utils/classes/LottieUtils";

const useStyles = makeStyles((theme) => ({
  //  ****************
  //  ** Containers **
  //  ****************
  forgotPasswordContainer: {
    width: "100vw",
    height: "90vh",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
    marginTop: "5vh",
    userSelect: "none",
    name: theme.name,
    color: theme.palette.lottie.plainBackground,
  },

  imgContainer: {
    backgroundColor: theme.palette.lottie.plainBackground,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "10px 0px 0px 10px",
    [theme.breakpoints.down("sm")]: { display: "none", },
  },

  forgotPasswordForm: {
    backgroundColor: theme.palette.lottie.secondaryPlainBackground,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "0px 10px 10px 0px",
  },

  //  ********************
  //  ** Form Container **
  //  ********************
  avatar: {
    margin: theme.spacing(0),
    width: "100px",
    height: "auto",
  },

  form: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },

  textField: {
    width: "80%",

    "& label.MuiFormLabel-root": { color: theme.palette.lottie.plainBackground, },

    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: theme.palette.lottie.plainBackground, },
      "&:hover fieldset": { borderColor: theme.palette.lottie.plainBackground, },
      "&.Mui-focused fieldset": { borderColor: "red", },
    },
  },

  textFieldInput: { color: theme.palette.lottie.plainBackground, },

  floatingButton: {
    margin: theme.spacing(3, 0, 2),
    transition: "transform 0.30s",
    "&:hover": { transform: "translateY(-5px)", },
  },

  secondaryButton: { color: theme.palette.lottie.plainBackground, },
}));

const PasswordReset = () => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const history = useHistory();

  const [email, setEmail] = useState("");

  const currentIcon = localStorage.getItem("theme") ? iconDark : iconLight;

  

  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => { document.title = "BestZap"; }, []);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // ***---- Validations ----***
    if (!ValidationUtils.validateEmail(email)) {
      toast.info(i18n.t("passwordReset.validations.invalidEmail"));
    }

    else {
      try {
        const { data } = await api.post("/passwordReset", { email });
        
        if (data.type === "warning") {
          toast.info(i18n.t(`backendErrors.${data.msg}`));
          return;
        }

        if (data.hash) {
          toast.success(i18n.t("passwordReset.toasts.success"));
          history.push(`/passwordResetValidation?hash=${data.hash}`);
          return;
        }
      } catch (exception) {
        console.log("Password Reset Handle Submit Exception:", exception);
        toastError(exception);
      }
    }
  };

  const handleChangeInput = (event) => { setEmail(event.target.value); };

  const handleBack = () => { history.push(DefaultValuesUtils.ROUTES[102]); };



  //  ************
  //  ** Return **
  //  ************
  return (
    <Container className={classes.forgotPasswordContainer} component="main">
      <CssBaseline />

      <div className={classes.imgContainer}>
        <Lottie
          options={{ ...LottieUtils.ANIMATION_PROPERTIES, animationData: ForgotPasswordLottie }}
          isClickToPauseDisabled={true}
          style={{ width: "80.00%", height: "80.00%", }}
        />
      </div>

      <div className={classes.forgotPasswordForm}>
        <img className={classes.avatar} src={currentIcon} alt="BestZap Logo" />

        <Typography component="h1" variant="h3">
          {i18n.t("passwordReset.title")}
        </Typography>

        <Typography component="h2" variant="h6">
          <br />
          {i18n.t("passwordReset.subtitle")}
          <br /><br />
        </Typography>

        <form className={classes.form} noValidate onSubmit={handleSubmit}>
          <TextField
            className={classes.textField}
            label={i18n.t("passwordReset.form.email")}
            autoComplete="off"
            variant="outlined"
            margin="normal"
            name="email"
            type="email"
            value={email}
            onChange={handleChangeInput}
            InputProps={{
              startAdornment: (<InputAdornment position="start"><AccountCircle /></InputAdornment>),
              className: classes.textFieldInput
            }}

            autoFocus
            required
          />

          <Button
            type="submit"
            style={{ width: "80%" }}
            variant="contained"
            color="primary"
            className={classes.floatingButton}
          >
            {i18n.t("passwordReset.buttons.submit")}
          </Button>

          <Button
            type="button"
            style={{ width: "80%" }}
            variant="outlined"
            color="primary"
            className={`${classes.floatingButton} ${classes.secondaryButton}`}
            onClick={handleBack}
          >
            {i18n.t("passwordReset.buttons.back")}
          </Button>
        </form>
      </div>
    </Container>
  );
};

export default PasswordReset;
